import type { TelemetrySample, AlarmEvent } from '../../store/Database';
import type { AppState } from '../../types/state';
import { DRIVE_PROFILES } from '../../config/profiles';
import { RULCalculator } from '../predictive/RULCalculator';

interface ReportInput {
  state: AppState;
  samples: TelemetrySample[];
  alarms: AlarmEvent[];
}

export class ReportGenerator {
  static async generate(input: ReportInput): Promise<void> {
    const { default: jsPDF } = await import('jspdf');

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentW = pageW - 2 * margin;
    let y = margin;

    const profile = DRIVE_PROFILES[input.state.selectedProfileKey];
    const rul = new RULCalculator();
    if (profile) rul.setProfile(profile);
    const now = new Date();
    const vendorLabel = profile?.vendor ?? 'Desconhecido';
    const modelLabel =
      profile?.name ?? input.state.selectedProfileKey ?? 'Perfil não disponível';

    const drawHeader = () => {
      pdf.setFillColor(8, 12, 20);
      pdf.rect(0, 0, pageW, 35, 'F');

      pdf.setFontSize(22);
      pdf.setTextColor(0, 217, 255);
      pdf.text('PREVDRIVE', margin, 18);

      pdf.setFontSize(8);
      pdf.setTextColor(143, 156, 174);
      pdf.text('Relatorio de Diagnostico Preditivo', margin, 25);

      const rightText = `${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR')}`;
      pdf.setFontSize(8);
      pdf.setTextColor(92, 107, 128);
      pdf.text(rightText, pageW - margin - pdf.getTextWidth(rightText), 18);

      pdf.setFontSize(8);
      pdf.setTextColor(92, 107, 128);
      const verText = 'v1.0 Enterprise';
      pdf.text(verText, pageW - margin - pdf.getTextWidth(verText), 25);

      pdf.setDrawColor(0, 0, 0);
      pdf.setFillColor(0, 217, 255);
      pdf.rect(margin, 32, contentW, 0.5, 'F');
    };

    const drawSectionTitle = (title: string) => {
      y += 6;
      pdf.setFontSize(11);
      pdf.setTextColor(0, 217, 255);
      pdf.text(title, margin, y);
      y += 1;
      pdf.setDrawColor(0, 217, 255);
      pdf.setLineWidth(0.3);
      pdf.line(margin, y, pageW - margin, y);
      y += 5;
    };

    const drawTable = (
      headers: string[],
      rows: string[][],
      colWidths: number[],
      startY: number,
    ): number => {
      const rowH = 5.5;
      let curY = startY;

      const drawRow = (cells: string[], isHeader: boolean) => {
        if (curY + rowH > pageH - margin) {
          pdf.addPage();
          curY = margin;
        }

        if (isHeader) {
          pdf.setFillColor(0, 217, 255);
          pdf.setTextColor(8, 12, 20);
        } else {
          pdf.setFillColor(16, 22, 37);
          pdf.setTextColor(243, 246, 250);
        }

        let x = margin;
        cells.forEach((cell, i) => {
          pdf.rect(x, curY, colWidths[i], rowH, isHeader ? 'F' : 'FD');
          pdf.setFontSize(7);
          pdf.text(cell, x + 1, curY + 3.8);
          x += colWidths[i];
        });

        curY += rowH;
      };

      drawRow(headers, true);
      rows.forEach((row) => drawRow(row, false));

      return curY;
    };

    // --- Page 1: Header ---
    drawHeader();
    y = 40;

    // --- Device info card ---
    pdf.setFillColor(16, 22, 37);
    pdf.roundedRect(margin, y, contentW, 18, 2, 2, 'F');
    pdf.setFontSize(9);
    pdf.setTextColor(143, 156, 174);
    pdf.text(`Fabricante: ${vendorLabel}`, margin + 3, y + 5);
    pdf.text(`Modelo: ${modelLabel}`, margin + 3, y + 10);
    pdf.text(`Slave ID: ${input.state.slaveId}`, margin + 3, y + 15);

    const modeStr = input.state.connectionMode === 'serial' ? 'RS-485 Conectado' : 'Simulacao';
    pdf.text(`Modo: ${modeStr}`, margin + contentW / 2, y + 5);
    const tripStr = input.state.activeTrip ?? 'Nenhum';
    pdf.text(`Trip Ativo: ${tripStr}`, margin + contentW / 2, y + 10);
    const statusColor = input.state.activeTrip ? '#ff4757' : '#00ff9d';
    pdf.setTextColor(243, 246, 250);
    pdf.text('Status:', margin + contentW / 2, y + 15);
    pdf.setTextColor(statusColor === '#ff4757' ? 255 : 0, statusColor === '#ff4757' ? 71 : 255, statusColor === '#ff4757' ? 87 : 157);
    const statusLabel = input.state.activeTrip ? 'CRITICO' : 'OPERACIONAL';
    pdf.text(statusLabel, margin + contentW / 2 + pdf.getTextWidth('Status: ') + 1, y + 15);

    // --- Summary Gauges ---
    y += 24;
    drawSectionTitle('Parametros em Tempo Real');

    const gaugeColW = contentW / 4;
    const gaugeData = [
      { label: 'Frequencia', value: `${input.state.currentFreq.toFixed(1)} Hz`, unit: '' },
      { label: 'Corrente', value: `${input.state.currentAmp.toFixed(1)} A`, unit: '' },
      { label: 'Tensao CC', value: `${input.state.dcVoltage.toFixed(0)} V`, unit: '' },
      { label: 'Temp. Dissipador', value: `${input.state.heatsinkTemp.toFixed(1)} °C`, unit: '' },
    ];

    const gaugeH = 20;
    let gx = margin;
    gaugeData.forEach((g) => {
      pdf.setFillColor(22, 30, 49);
      pdf.roundedRect(gx, y, gaugeColW - 2, gaugeH, 2, 2, 'F');

      pdf.setFontSize(7);
      pdf.setTextColor(143, 156, 174);
      pdf.text(g.label, gx + 2, y + 5);

      pdf.setFontSize(14);
      pdf.setTextColor(243, 246, 250);
      pdf.text(g.value, gx + 2, y + 16);

      gx += gaugeColW;
    });

    y += gaugeH + 4;

    // --- Component Health ---
    drawSectionTitle('Vida Util dos Componentes');

    const fanMain = rul.calculateFanMain(input.state.fanMainHours);
    const fanCtrl = rul.calculateFanControl(input.state.fanControlHours);
    const cap = rul.calculateCapacitor(input.state.daysOffline, 'normal', input.state.fanMainHours);
    const igbt = rul.calculateIGBT(input.state.fanMainHours, 50000, input.state.heatsinkTemp);
    const bearing = rul.calculateBearing(input.state.fanMainHours, 1.0, 1.0);
    const healthItems = [fanMain, fanCtrl, cap, igbt, bearing];

    healthItems.forEach((item) => {
      if (y + 60 > pageH - margin) {
        pdf.addPage();
        drawHeader();
        y = margin + 10;
        drawSectionTitle('Vida Util dos Componentes');
      }

      pdf.setFillColor(16, 22, 37);
      pdf.roundedRect(margin, y, contentW, 10, 1.5, 1.5, 'F');

      pdf.setFontSize(8);
      pdf.setTextColor(243, 246, 250);
      pdf.text(item.component, margin + 2, y + 4);

      const statusColors: Record<string, [number, number, number]> = {
        ok: [0, 255, 157],
        warn: [255, 183, 0],
        critical: [255, 71, 87],
      };
      const c = statusColors[item.status] ?? [143, 156, 174];

      pdf.setFontSize(7);
      pdf.setTextColor(c[0], c[1], c[2]);
      const statusTxt = `${item.remainingHours.toLocaleString()}h restantes (${item.percentageUsed.toFixed(0)}% usado) - ${item.status.toUpperCase()}`;
      pdf.text(statusTxt, margin + contentW - pdf.getTextWidth(statusTxt) - 2, y + 4);

      const barY = y + 6.5;
      const barW = contentW - 4;
      const barH = 2.5;

      pdf.setFillColor(8, 12, 20);
      pdf.roundedRect(margin + 2, barY, barW, barH, 1, 1, 'F');
      pdf.setFillColor(c[0], c[1], c[2]);
      pdf.roundedRect(margin + 2, barY, barW * (item.percentageUsed / 100), barH, 1, 1, 'F');

      y += 12;
    });

    y += 2;

    // --- Active Rules ---
    drawSectionTitle('Regras e Alarmes');

    const ruleRows = input.alarms.map((a) => [
      a.timestamp.toLocaleTimeString('pt-BR'),
      a.severity.toUpperCase(),
      a.alarmCode,
      a.acknowledged ? 'Reconhecido' : 'Pendente',
    ]);

    const alarmTableRows = input.samples.slice(-10).reverse().map((s) => [
      s.timestamp.toLocaleTimeString('pt-BR'),
      `${s.frequency.toFixed(1)} Hz`,
      `${s.current.toFixed(1)} A`,
      `${s.dcVoltage.toFixed(0)} V`,
      `${s.temperature.toFixed(1)} °C`,
      `${s.load.toFixed(0)}%`,
    ]);

    if (ruleRows.length > 0) {
      y = drawTable(
        ['Hora', 'Severidade', 'Codigo', 'Status'],
        ruleRows,
        [25, 30, 35, contentW - 90],
        y,
      );
      y += 3;
    } else {
      pdf.setFontSize(8);
      pdf.setTextColor(92, 107, 128);
      pdf.text('Nenhum alarme registrado no periodo.', margin, y + 4);
      y += 8;
    }

    // --- Telemetry Data ---
    if (alarmTableRows.length > 0) {
      y += 2;
      drawSectionTitle('Ultimas Amostras de Telemetria');

      const sampleStartY = y;
      if (sampleStartY + alarmTableRows.length * 5.5 > pageH - margin) {
        pdf.addPage();
        y = margin + 5;
        drawSectionTitle('Ultimas Amostras de Telemetria');
      }

      y = drawTable(
        ['Hora', 'Freq', 'Corrente', 'Tensao CC', 'Temperatura', 'Carga'],
        alarmTableRows.slice(0, 10),
        [22, 22, 25, 25, 25, contentW - 119],
        y,
      );
    }

    // --- Footer ---
    y = pageH - 8;
    pdf.setFontSize(7);
    pdf.setTextColor(92, 107, 128);
    const footerText = `PREVDRIVE v1.0 Enterprise - Gerado em ${now.toLocaleString('pt-BR')} - Confidencial`;
    pdf.text(footerText, margin, y);

    pdf.save(`prevdrive-relatorio-${Date.now()}.pdf`);
  }
}
